import React, { useState, useRef, useEffect } from 'react';
import { TIME_SLOTS_30_MIN, END_TIME_SLOTS_30_MIN, timeToMinutes } from '../utils/timezoneUtils';
import './TimeSlotPicker.scss';

interface TimeSlotPickerProps {
  value: string;
  onChange: (newValue: string) => void;
  type: 'start' | 'end';
  minTime?: string;
  maxTime?: string;
  disabled?: boolean;
}

export const TimeSlotPicker: React.FC<TimeSlotPickerProps> = ({
  value,
  onChange,
  type,
  minTime,
  maxTime,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const rawOptions = type === 'start' ? TIME_SLOTS_30_MIN : END_TIME_SLOTS_30_MIN;

  const minMinutes = minTime ? timeToMinutes(minTime) : undefined;
  const maxMinutes = maxTime ? timeToMinutes(maxTime) : undefined;

  // Close when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Scroll to active option when opening
  useEffect(() => {
    if (isOpen && listRef.current) {
      const activeEl = listRef.current.querySelector('.time-option.active') as HTMLElement | null;
      if (activeEl) {
        activeEl.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [isOpen]);

  const handleSelect = (timeStr: string) => {
    onChange(timeStr);
    setIsOpen(false);
  };

  return (
    <div className={`time-slot-picker-container ${disabled ? 'disabled' : ''}`} ref={containerRef}>
      <button
        type="button"
        className={`time-display-btn ${isOpen ? 'open' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span>{value || (type === 'start' ? '09:00' : '17:00')}</span>
      </button>

      {isOpen && (
        <div className="time-dropdown-menu" ref={listRef} role="listbox">
          {rawOptions.map((opt) => {
            const optMins = timeToMinutes(opt);
            const isTooEarly = minMinutes !== undefined && optMins < minMinutes;
            const isTooLate = maxMinutes !== undefined && optMins > maxMinutes;
            const isOptionDisabled = isTooEarly || isTooLate;
            const isSelected = opt === value;

            return (
              <button
                key={opt}
                type="button"
                className={`time-option ${isSelected ? 'active' : ''} ${
                  isOptionDisabled ? 'disabled' : ''
                }`}
                onClick={() => !isOptionDisabled && handleSelect(opt)}
                disabled={isOptionDisabled}
                role="option"
                aria-selected={isSelected}
              >
                {opt}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
